import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Journey,
  PageAbsolutePath,
  PageRelativePath,
  ProductChannel
} from '../../../../consts/Common';
import RenewalsQuoteCard from './RenewalsQuoteCard';
import {
  LinkedProduct,
  ProductData,
  ProductFilter,
  ProductType,
  SelectedLinkedProduct
} from '../../../common/CommonTypes';
import { PaymentMethod } from '../../../common/Constants';
import { getFormattedDate } from '../../../common/HelperMethods';
import {
  getProductInstalmentTotalPriceByPaymentMethod,
  getBenefits,
  getNoOfInstallments,
  getSelectedProducts,
  hasPaymentType
} from '../../../common/ProductMethods';
import { ProductApi } from '../../../../api/ProductApi';
import { SiteContext } from '../../../../context/SiteContext';
import PageHeader from '../../../common/PageHeader';
import { ButtonBack } from '../../../common/Button/ButtonBack';
import { ButtonContinue } from '../../../common/Button/ButtonContinue';
import { useJourney } from '../../Layout';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import LoadingContainer from '../../../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';

const RenewalsQuote = (): JSX.Element => {
  const [error, setError] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<number>();
  const [selectedQuoteType, setSelectedQuoteType] = useState<number>();
  const [selectedLinkedProducts, setSelectedLinkedProducts] = useState<
    SelectedLinkedProduct[]
  >([]);

  const { journey, onUpdateProducts, onUpdateProductDocuments, onPageChange } =
    useJourney();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [hasProducts, setHasProducts] = useState(false);
  const { dealerCode, token, products, vehicle, promoCode } = journey;
  const quotesNotFound = !products || products.length === 0;
  const noOfInstallments = getNoOfInstallments(products);
  const { site } = useContext(SiteContext);
  const cms = site.content.quotePage;
  const navigate = useNavigate();
  const isCallCentreJourney = site.journeyType === Journey.CallCentre;

  const processingMessage = t('renewals-quote.processing-message');
  const noMatchingQuotesTitle = t('renewals-quote.no-matching-quotes-title');
  const noQuotesExistsForPromo = t('renewals-quote.no-quotes-exists-for-promo');
  const noMatchingQuotesText = t('renewals-quote.no-matching-quotes-title');
  const noProductsAvailableText = t(
    'renewals-quote.no-products-available-text'
  );
  const noProductsAvailableSubText = t(
    'renewals-quote.no-products-available-sub-text'
  );
  const contactUsText = t('renewals-quote.contact-us-text');
  const contactUsNumber = t('renewals-quote.contact-us-number');
  const buttonCheckoutText = t('renewals-quote.button-checkout-text');
  const mandatoryQuoteError = t('renewals-quote.mandatory-quote-error');
  const buttonBackText = t('renewals-quote.button-back-text');
  useEffect(() => {
    window.scrollTo(0, 0);

    if (products && products.length > 0) {
      const result = getSelectedProducts(products);

      if (result && result.length > 0) {
        const selectedItem = result[0];
        setSelectedQuote(selectedItem.selectedCoverLevelId);
        setSelectedQuoteType(selectedItem.productTypeId);
      }

      const slp: SelectedLinkedProduct[] = products
        .map((q) => q.selectedLinkedProducts || [])
        .reduce((a, b) => a.concat(b), []);

      setSelectedLinkedProducts(slp);
    } else {
      loadProducts();
    }
  }, []);

  const loadProducts = () => {
    const controller = new AbortController();

    const successCallback = (data: ProductData) => {
      const retrivedProducts: ProductType[] = data.products.map((product) => ({
        ...product,
        isSelected: false,
        productVariants: product.productVariants.map((variant) => ({
          ...variant,
          isSelected: false
        }))
      }));

      onUpdateProducts(retrivedProducts);
      onUpdateProductDocuments(data.documents);
      setHasProducts(data.hasProducts);
      setLoading(false);
    };

    const failureCallback = () => {
      console.log('get products failed');
      setHasProducts(false);
      setLoading(false);
    };

    const productFilter: ProductFilter = {
      dealerCode: dealerCode,
      firstRegistrationDate: vehicle.vehicleDetails.registrationDate,
      vehiclePurchaseDate: getFormattedDate(vehicle.vehicleDeliveryDate),
      vehicleValue: vehicle.vehiclePurchasePrice,
      currentMileage: vehicle.currentMileage ?? 0,
      modelTrimId: vehicle.vehicleDetails.modelTrimId,
      fuelTypeId: vehicle.vehicleDetails.fuelTypeCode,
      engineSize: parseInt(vehicle.vehicleDetails.engineSize),
      enginePowerBHP: vehicle.vehicleDetails.enginePowerBHP ?? 0,
      enginePowerKWH: vehicle.vehicleDetails.enginePowerKWH ?? 0,
      driveTypeId: vehicle.vehicleDetails.driveTypeId?.toString() ?? '',
      vrn: vehicle.vehicleDetails.vrn,
      channel: ProductChannel.RenewalJourney,
      promoCode: promoCode,
      countryCode: site.countryCode
    };

    setLoading(true);
    new ProductApi().getProducts(
      productFilter,
      token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onSelectionChange = (id: number, type: number) => {
    setSelectedQuote(id);
    setSelectedQuoteType(type);
  };

  const onLinkedOptionalQuoteSelectionChange = (
    quoteCardId: number,
    linkedProductTypeId: number
  ) => {
    let updatedLinkedProducts: SelectedLinkedProduct[] = [];

    const exists = selectedLinkedProducts.find(
      (slp) =>
        slp.quoteCardId === quoteCardId &&
        slp.linkedProductTypeId === linkedProductTypeId
    );

    if (exists) {
      //remove it
      updatedLinkedProducts = selectedLinkedProducts.filter(
        (slp) =>
          !(
            slp.quoteCardId === quoteCardId &&
            slp.linkedProductTypeId === linkedProductTypeId
          )
      );
    } else {
      //add it
      updatedLinkedProducts = [
        ...selectedLinkedProducts,
        { quoteCardId, linkedProductTypeId }
      ];
    }

    setSelectedLinkedProducts(updatedLinkedProducts);
  };

  const updateSelectedProducts = () => {
    const updatedProducts = products.map((q) =>
      q.productId === selectedQuote
        ? {
            ...q,
            isSelected: true,
            policyStartDate: undefined,
            selectedTyreTypeId: undefined,
            selectedClaimLimit: undefined,
            selectedCoverLevelId: selectedQuote,
            selectedPaymentMethod: undefined,
            selectedLinkedProducts: selectedLinkedProducts,
            productVariants: q.productVariants.map((variant) =>
              variant.isSelected === true
                ? { ...variant, isSelected: false }
                : variant
            )
          }
        : {
            ...q,
            isSelected: false,
            policyStartDate: undefined,
            selectedTyreTypeId: undefined,
            selectedClaimLimit: undefined,
            selectedCoverLevelId: undefined,
            selectedPaymentMethod: undefined,
            selectedLinkedProductTypes: undefined,
            selectedLinkedProducts: undefined,
            productVariants: q.productVariants.map((variant) =>
              variant.isSelected === true
                ? { ...variant, isSelected: false }
                : variant
            )
          }
    );
    onUpdateProducts(updatedProducts);
  };

  const onBack = () => {
    updateSelectedProducts();
    goBack();
    onPageChange(PageAbsolutePath.Renewals.Quote, false);
  };

  const goBack = () => {
    navigate(PageRelativePath.VehicleDetails);
  };

  const onCheckout = () => {
    if (validate() === true) {
      updateSelectedProducts();
      if (isCallCentreJourney) navigate(PageRelativePath.PersonalDetails);
      else navigate(PageRelativePath.Summary);
      onPageChange(PageAbsolutePath.Renewals.Quote, true);
    }
  };

  const validate = (): boolean => {
    let isValid = true;

    if (
      CommonValidation.isEmpty(selectedQuote) ||
      (selectedQuote === 0 && selectedQuoteType === 0)
    ) {
      isValid = false;
      setError(mandatoryQuoteError);
    }
    return isValid;
  };

  const getLinkedProducts = (quote: ProductType) => {
    const linkedProducts = quote.productVariants
      .map((variant) => variant.linkedProducts)
      .reduce((a, b) => a.concat(b), [] as LinkedProduct[]);

    return linkedProducts;
  };

  const getDefaultPaymentMethod = (quote: ProductType) => {
    let result: string = isCallCentreJourney
      ? PaymentMethod.Single
      : PaymentMethod.Monthly;

    if (hasPaymentType([quote], PaymentMethod.Subscription)) {
      result = PaymentMethod.Subscription;
    }

    return result;
  };

  const renderProducts = () => {
    return products.map((q) => {
      const monthlyPrice = getProductInstalmentTotalPriceByPaymentMethod(
        q,
        PaymentMethod.Monthly,
        noOfInstallments,
        selectedLinkedProducts
      );

      const singlePrice = getProductInstalmentTotalPriceByPaymentMethod(
        q,
        PaymentMethod.Single,
        noOfInstallments,
        selectedLinkedProducts
      );

      const subscriptionPrice = getProductInstalmentTotalPriceByPaymentMethod(
        q,
        PaymentMethod.Subscription,
        noOfInstallments,
        selectedLinkedProducts
      );

      const keyBenefits = isCallCentreJourney
        ? []
        : getBenefits(cms.productTypeCustomisations, q, selectedLinkedProducts);

      return (
        <RenewalsQuoteCard
          key={`quote-${q.productTypeId}-${q.renewalGroupId}`}
          id={q.renewalGroupId}
          productId={q.productId}
          keyBenefitLists={keyBenefits}
          quoteTitle={q.productVariants[0].customerFriendlyName}
          priceMonthly={
            monthlyPrice.firstInstallmentPrice === 0 ? undefined : monthlyPrice
          }
          priceSingle={
            singlePrice.firstInstallmentPrice === 0
              ? undefined
              : singlePrice.firstInstallmentPrice
          }
          priceSubscription={
            subscriptionPrice.firstInstallmentPrice === 0
              ? undefined
              : subscriptionPrice.firstInstallmentPrice
          }
          quoteType={q.productTypeId}
          isPaymentDone={false}
          showMoreInfo={!isCallCentreJourney}
          showSelectOption={true}
          selectedQuote={selectedQuote}
          linkedQuotes={getLinkedProducts(q)}
          hideBenefits={isCallCentreJourney}
          defaultPaymentMethod={getDefaultPaymentMethod(q)}
          selectedOptionalLinkedQuotes={selectedLinkedProducts}
          noOfInstallments={noOfInstallments}
          productVariants={q.productVariants}
          showAllPricingOptions={isCallCentreJourney}
          isVariablePrice={q.isVariablePrice}
          onSelectionChange={onSelectionChange}
          onLinkedOptionalQuoteSelectionChange={
            onLinkedOptionalQuoteSelectionChange
          }
          culture={site.cultureCode}
        />
      );
    });
  };

  return (
    <LoadingContainer loading={loading} message={processingMessage}>
      <div>
        {quotesNotFound === false && (
          <>
            <PageHeader
              title={cms.title}
              description={cms.description}
              descriptionClass='p2'
              information={cms.information}
              warning={cms.warning}
            />
            <p className='p2 bold m1-bottom m2-top'>{cms.selectionText}</p>
          </>
        )}
        {quotesNotFound && isCallCentreJourney && (
          <>
            <h3>{noMatchingQuotesTitle}</h3>
            {hasProducts && (
              <p className='m1-bottom'>{noQuotesExistsForPromo}</p>
            )}
          </>
        )}
        {quotesNotFound && !isCallCentreJourney && (
          <>
            <h3>{noMatchingQuotesText}</h3>
            <p className='m1-bottom'>{noProductsAvailableText}</p>
            <p className='m1-bottom'>{noProductsAvailableSubText}</p>
            <p>
              {contactUsText}
              <a href={`tel:${contactUsNumber}`}>{contactUsNumber}</a>.
            </p>
          </>
        )}
        <div>{renderProducts()}</div>
        <div className='warning-text m05-bottom'>{error}</div>
        <div className='flex-row form-nav'>
          <ButtonBack name='btnBack' text={buttonBackText} onClick={onBack} />
          {quotesNotFound === false && (
            <ButtonContinue
              name='btn-checkout'
              text={buttonCheckoutText}
              onClick={onCheckout}
            />
          )}
        </div>
      </div>
    </LoadingContainer>
  );
};

export default RenewalsQuote;
